import axios from "../axios/AxiosInstance";

const CACHE_TTL_MS = 15000;
// React StrictMode démonte/remonte les effets en développement. Ce court délai
// laisse le remontage se réabonner sans conserver indéfiniment une requête
// réellement abandonnée.
const ABORT_GRACE_MS = 250;
const cache = new Map();
const pending = new Map();

function canceledError() {
  const error = new Error("Requête annulée");
  error.name = "AbortError";
  error.code = "ERR_CANCELED";
  return error;
}

function requestKey(params) {
  const token =
    typeof localStorage === "undefined"
      ? ""
      : localStorage.getItem("accessToken") || "";
  return [token, params.candidateId, params.clientId, params.date]
    .map((value) => value || "")
    .join("|");
}

function subscribe(entry, key, signal) {
  if (signal?.aborted) return Promise.reject(canceledError());

  entry.subscribers += 1;
  if (entry.abortTimer) {
    clearTimeout(entry.abortTimer);
    entry.abortTimer = null;
  }

  return new Promise((resolve, reject) => {
    let finished = false;
    const release = () => {
      if (finished) return;
      finished = true;
      signal?.removeEventListener("abort", onAbort);
      entry.subscribers -= 1;
      if (entry.subscribers === 0 && !entry.settled) {
        entry.abortTimer = setTimeout(() => {
          if (
            entry.subscribers === 0 &&
            !entry.settled &&
            pending.get(key) === entry
          ) {
            entry.controller.abort();
            pending.delete(key);
          }
        }, ABORT_GRACE_MS);
      }
    };
    const onAbort = () => {
      release();
      reject(canceledError());
    };

    signal?.addEventListener("abort", onAbort, { once: true });
    entry.promise.then(
      (rows) => {
        if (finished) return;
        release();
        resolve(rows);
      },
      (error) => {
        if (finished) return;
        release();
        reject(error);
      },
    );
  });
}

export function clearInterviewCache() {
  cache.clear();
}

export function resetInterviewRequestState() {
  cache.clear();
  pending.forEach((entry) => entry.controller.abort());
  pending.clear();
}

export function loadInterviews({
  candidateId,
  clientId,
  date,
  signal,
  force = false,
} = {}) {
  const params = {
    ...(candidateId ? { candidateId: String(candidateId) } : {}),
    ...(clientId ? { clientId: String(clientId) } : {}),
    ...(date ? { date: String(date) } : {}),
  };
  const key = requestKey(params);
  const saved = cache.get(key);
  if (!force && saved && Date.now() - saved.savedAt < CACHE_TTL_MS) {
    return signal?.aborted
      ? Promise.reject(canceledError())
      : Promise.resolve(saved.rows);
  }

  let entry = pending.get(key);
  if (!entry) {
    const controller = new AbortController();
    entry = {
      controller,
      subscribers: 0,
      settled: false,
      abortTimer: null,
      promise: null,
    };
    entry.promise = axios
      .get("entretien", { params, signal: controller.signal })
      .then(({ data }) => {
        if (!Array.isArray(data)) {
          throw new Error("Réponse d’entretiens invalide");
        }
        cache.set(key, { rows: data, savedAt: Date.now() });
        return data;
      })
      .finally(() => {
        entry.settled = true;
        if (entry.abortTimer) clearTimeout(entry.abortTimer);
        if (pending.get(key) === entry) pending.delete(key);
      });
    pending.set(key, entry);
  }

  return subscribe(entry, key, signal);
}
